import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { StepConfiguration, StepStatusState } from "./stepsInterfaces";
import {
  findNextStep,
  findPreviousStep,
  findStep,
  getPrecedingSteps,
  getSubsequentSteps,
  withStepUri,
} from "./stepsUtils";

import { Step } from "@storebrand-digital/insurance-stepper";
import { isDefined } from "Utils/functions";

const initialState: StepStatusState = {
  steps: [],
};

const stepSlice = createSlice({
  name: "steps",
  initialState,
  reducers: {
    registerSteps: (state, action: PayloadAction<StepConfiguration>) => {
      resetStepsState(state);
      setStepConfiguration(state, action.payload);
    },
    nextStep: (state) => {
      setNextStep(state);
    },
    prevStep: (state) => {
      setPrevStep(state);
    },
    activateStep: (state, action: PayloadAction<string>) => {
      setActiveStep(state, action.payload);
    },
    updateStep: (
      state,
      action: PayloadAction<{ stepId: string; stepUpdate: Partial<Step> }>,
    ) => {
      const { stepId, stepUpdate } = action.payload;
      setUpdatedStep(state, stepId, stepUpdate);
    },
    clearActiveStep: (state) => {
      state.activeStepId = initialState.activeStepId;
    },
    clearStepStatus: (state) => {
      resetStepsState(state);
    },
    activateStartStep: (state) => {
      const { startStepId } = state;
      if (isDefined(startStepId)) {
        setActiveStep(state, startStepId);
      }
    },
    updateActiveStep: (state, action: PayloadAction<Partial<Step>>) => {
      setUpdatedStep(state, state.activeStepId, action.payload);
    },
  },
});

const setStepConfiguration = (
  state: StepStatusState,
  stepConfiguration: StepConfiguration,
): void => {
  const { steps, startStepId, path } = stepConfiguration;

  state.steps = steps.map((step) => withStepUri(step, path));

  const startStep = findStep(steps, startStepId) ?? steps[0];
  state.startStepId = startStep?.id;
};

const setNextStep = (state: StepStatusState): void => {
  const activeStep = findStep(state.steps, state.activeStepId);
  if (isDefined(activeStep)) {
    activeStep.complete = true;
    const nextStep = findNextStep(state.steps, state.activeStepId);
    if (isDefined(nextStep)) {
      state.activeStepId = nextStep.id;
    } else {
      state.activeStepId = undefined;
    }
  }
};

const setPrevStep = (state: StepStatusState): void => {
  const activeStep = findStep(state.steps, state.activeStepId);
  if (isDefined(activeStep)) {
    const previousStep = findPreviousStep(state.steps, state.activeStepId);
    if (isDefined(previousStep)) {
      state.activeStepId = previousStep.id;
      previousStep.complete = false;
    } else {
      state.activeStepId = undefined;
    }
  }
};

const setActiveStep = (state: StepStatusState, stepId: string): void => {
  const step = findStep(state.steps, stepId);
  if (isDefined(step)) {
    step.complete = false;
    state.activeStepId = step.id;
    completePrecedingSteps(state, stepId);
    resetSubsequentStepStatuses(state, stepId);
  }
};

const completePrecedingSteps = (state: StepStatusState, stepId: string): void =>
  getPrecedingSteps(state.steps, stepId).forEach((step) => {
    step.complete = true;
  });

const resetSubsequentStepStatuses = (
  state: StepStatusState,
  stepId: string,
): void =>
  getSubsequentSteps(state.steps, stepId).forEach((step) => {
    step.complete = false;
  });

const setUpdatedStep = (
  state: StepStatusState,
  stepId: string | undefined,
  stepUpdate: Partial<Step>,
): void => {
  const step = findStep(state.steps, stepId);
  if (isDefined(step)) {
    Object.assign(step, stepUpdate);
  }
};

const resetStepsState = (state: StepStatusState): void => {
  state.steps = initialState.steps;
  state.activeStepId = initialState.activeStepId;
  state.startStepId = initialState.startStepId;
};

export const {
  registerSteps,
  nextStep,
  prevStep,
  activateStep,
  activateStartStep,
  clearActiveStep,
  updateActiveStep,
  updateStep,
  clearStepStatus,
} = stepSlice.actions;
export const stepStatus = stepSlice.reducer;
